import { useEffect, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { buildBreadcrumbs, buildLocalService, buildLocalBusiness } from "@/lib/jsonLd";
import { ArrowUpRight, ShieldCheck, Award, Clock } from "lucide-react";

/**
 * Local SEO city page — one URL per city in Loire-Atlantique.
 * Each city gets a unique editorial intro paragraph to avoid duplicate content.
 */

const CITIES = {
  nantes: {
    name: "Nantes",
    eyebrow: "Métropole",
    intro: "Cuisines hauss­manniennes, dressings d'appartements anciens, bibliothèques et agencements professionnels — j'interviens à Nantes intra-muros comme dans les communes voisines de la métropole. Mon atelier est calibré pour les contraintes des immeubles anciens : escaliers étroits, planchers vivants, hauteurs sous plafond généreuses qu'on traite avec proportion.",
    angle: "Métropole · centre-ville · immeubles anciens",
    materiaux: "Chêne, noyer, frêne — finitions huilées",
    delais: "Visite sous 5 jours ouvrés, devis sous 8 jours",
  },
  bouguenais: {
    name: "Bouguenais",
    eyebrow: "Sud-Loire",
    intro: "À Bouguenais et dans le sud de la métropole nantaise, je travaille en cuisines, dressings et agencements de maisons individuelles. Les programmes contemporains comme les rénovations de longère y trouvent leur compte — chaque chantier conserve son écriture propre, sans imposer une signature.",
    angle: "Maisons individuelles · longères · contemporain",
    materiaux: "Chêne, hêtre, mélaminé qualité atelier",
    delais: "Devis sous 8 jours, calendrier tenu",
  },
  "la-baule": {
    name: "La Baule",
    eyebrow: "Presqu'île",
    intro: "Villas balnéaires, résidences secondaires, agencements de location haut de gamme — à La Baule, l'enjeu n'est pas seulement esthétique : les matériaux doivent encaisser l'air marin et les usages intensifs d'été. Je travaille avec des essences et des finitions choisies pour durer sur la côte.",
    angle: "Villas · résidences secondaires · location premium",
    materiaux: "Chêne, frêne, finitions résistantes à l'air marin",
    delais: "Coordination compatible avec les saisons touristiques",
  },
  pornichet: {
    name: "Pornichet",
    eyebrow: "Presqu'île",
    intro: "À Pornichet, entre villas Belle Époque et résidences contemporaines, je conçois et pose des agencements sur-mesure qui respectent l'architecture existante. Cuisines, bibliothèques, claustras — la matière brute reste la signature, l'ostentation jamais.",
    angle: "Belle Époque · contemporain · matière franche",
    materiaux: "Chêne, noyer, finitions huilées",
    delais: "Devis sous 8 jours, calendrier ferme",
  },
  "le-pouliguen": {
    name: "Le Pouliguen",
    eyebrow: "Presqu'île",
    intro: "Au Pouliguen, dans les maisons de pêcheurs comme dans les villas en surplomb, j'interviens pour des agencements sur-mesure qui s'inscrivent dans l'histoire des lieux. Le bois travaillé avec patience tient les codes locaux sans tomber dans le pastiche.",
    angle: "Maisons de pêcheurs · villas · côte sauvage",
    materiaux: "Chêne, frêne, hêtre vapeur",
    delais: "Coordination directe avec maîtres d'œuvre locaux",
  },
  pornic: {
    name: "Pornic",
    eyebrow: "Côte de Jade",
    intro: "Pornic et la côte de Jade — un territoire où je travaille aussi bien pour des particuliers en résidence principale que pour des dossiers d'assurance sur des sinistres tempête ou dégât des eaux. La rigueur des constats et la tenue des délais comptent autant que le travail du bois lui-même.",
    angle: "Côte de Jade · sinistres tempête · résidences",
    materiaux: "Chêne, finitions extérieures résistantes",
    delais: "Constat sinistre sous 5 jours · décennale & RC Pro à jour",
  },
  "saint-brevin": {
    name: "Saint-Brevin",
    eyebrow: "Côte de Jade",
    intro: "Saint-Brevin-les-Pins — bord de mer, climat exigeant, programmes mixtes entre permanents et résidences secondaires. Je travaille les agencements en pensant durée et entretien : un bois noble bien posé tient quarante ans, à condition de respecter la matière dès le tracé.",
    angle: "Bord de mer · résidences mixtes · longévité",
    materiaux: "Chêne, frêne, finitions huilées spécifiques côte",
    delais: "Devis sous 8 jours, calendrier compatible saisons",
  },
};

const TRUST = [
  { icon: Award, label: "Compagnons du Devoir" },
  { icon: ShieldCheck, label: "Décennale & RC Pro à jour" },
  { icon: Clock, label: "Réponse sous 48 heures" },
];

export default function MenuisierVille() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/menuisier-/, "");
  const city = CITIES[slug];
  const [business, setBusiness] = useState(null);
  const [realisations, setRealisations] = useState([]);

  useEffect(() => {
    api.get("/site/business").then((r) => setBusiness(r.data)).catch(() => {});
    api.get("/realisations").then((r) => setRealisations((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  if (!city) return <Navigate to="/" replace />;

  const jsonLd = [
    buildLocalBusiness(business),
    buildLocalService({ city: city.name, slug }),
    buildBreadcrumbs([{ name: `Menuisier ${city.name}`, path: `/menuisier-${slug}` }]),
  ].filter(Boolean);

  return (
    <div data-testid={`ville-page-${slug}`}>
      <Seo
        title={`Menuisier agenceur à ${city.name} (Loire-Atlantique)`}
        description={`Luigi Chauveau, menuisier agenceur, intervient à ${city.name}. ${city.angle}. Cuisines, dressings, bibliothèques, remises en état après sinistre. Devis sous 8 jours.`}
        path={`/menuisier-${slug}`}
        jsonLd={jsonLd}
      />

      {/* ── Compact editorial header ── */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-10 md:pb-14 grid md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-7">
          <p className="text-eyebrow-gold mb-3">{city.eyebrow}</p>
          <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight fade-up">
            Menuisier agenceur <span className="italic text-primary">à {city.name}</span>.
          </h1>
        </div>
        <p className="md:col-span-5 text-[14.5px] leading-relaxed text-foreground/75 max-w-md md:justify-self-end fade-up fade-up-1">
          {city.intro}
        </p>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="h-px w-full bg-[#8A6A47]/30" />
      </div>

      {/* ── 3 facets ── */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid md:grid-cols-3 gap-px bg-border">
          {[
            { eyebrow: "i — Angle", title: city.angle, body: `Mon approche à ${city.name} prend en compte les contraintes du bâti et l'art de vivre local — sans copier-coller.` },
            { eyebrow: "ii — Matière", title: city.materiaux, body: "Le matériau dicte la finition. Pas l'inverse." },
            { eyebrow: "iii — Délais", title: city.delais, body: "La parole tenue, surtout quand l'expert et l'assuré attendent." },
          ].map((f, i) => (
            <div key={i} className="bg-background p-8 md:p-10 min-h-[220px] flex flex-col gap-4 fade-up" style={{ animationDelay: `${i * 90}ms` }}>
              <p className="text-eyebrow-gold">{f.eyebrow}</p>
              <h2 className="font-serif text-2xl md:text-[30px] leading-tight">{f.title}</h2>
              <p className="text-[13.5px] text-muted-foreground leading-relaxed mt-auto">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trust badges ── */}
      <section className="border-y border-border bg-muted/30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 md:py-8 flex flex-wrap items-center justify-center md:justify-between gap-x-10 gap-y-3">
          {TRUST.map((t, i) => (
            <div key={i} className="flex items-center gap-3">
              <t.icon size={16} className="text-[hsl(var(--gold))]" />
              <span className="text-[11.5px] tracking-[0.18em] uppercase text-foreground/80">{t.label}</span>
            </div>
          ))}
          <a href="tel:+33761051061" className="text-[11.5px] tracking-[0.2em] uppercase font-medium text-primary link-underline-static">
            07 61 05 10 61
          </a>
        </div>
      </section>

      {/* ── Realisations preview ── */}
      {realisations.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-6">
              <p className="text-eyebrow-gold mb-3">iv — Quelques projets</p>
              <h2 className="h-display text-4xl md:text-5xl leading-[1.02]">
                Le travail, <span className="serif-italic text-primary">livré</span>.
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {realisations.map((r) => (
              <Link key={r.id} to={`/realisations/${r.slug}`} className="group block">
                <div className="aspect-[4/3] overflow-hidden bg-[#F5F1EA] mb-5">
                  <img src={r.photo_couverture} alt={r.titre} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" />
                </div>
                <p className="text-[10px] tracking-[0.24em] uppercase text-foreground/55 mb-2">{r.annee} · {r.categorie}</p>
                <h3 className="font-serif text-xl md:text-2xl leading-tight group-hover:text-primary transition-colors">{r.titre}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="bg-foreground text-background">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <p className="text-eyebrow-gold mb-5">v — Engager une conversation</p>
            <h2 className="h-display text-5xl md:text-[88px] leading-[0.94] text-balance">
              Un projet à <span className="serif-italic text-[hsl(var(--gold-soft))]">{city.name}</span> ?
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 space-y-6">
            <p className="text-[14.5px] text-background/75 leading-relaxed">
              Décrivez votre projet en quelques mots. Réponse sous 48 heures, visite organisée dans la semaine.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-3 bg-background text-foreground px-6 py-4 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-primary hover:text-background transition-colors" data-testid={`ville-cta-${slug}`}>
              Formuler une demande <ArrowUpRight size={14} />
            </Link>
            <a href="tel:+33761051061" className="block text-[12px] tracking-[0.22em] uppercase text-background/70 hover:text-background">
              Ou appeler — 07 61 05 10 61
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
